import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

import {
  FAQ,
  FAQCategory,
  FAQs,
  IDocument,
  IStrapiDocument,
  IStrapiDocuments,
} from 'src/app/interfaces/strapi';

import { PageComponent } from 'src/app/shared/components/page/page.component';
import { UntypedFormBuilder, UntypedFormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import StrapiService from 'src/app/services/strapi.service';
import { NgIf, NgFor } from '@angular/common';
import { RouterLink } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { AuthService } from '../../../services/auth.service';
import { RenderMarkdownComponent } from '../../../shared/components/render-markdown/render-markdown.component';

@Component({
    selector: 'app-faqs',
    templateUrl: './faqs.component.html',
    styleUrls: ['./faqs.component.scss'],
  changeDetection:ChangeDetectionStrategy.OnPush,
    imports: [
        BreadcrumbsComponent,
        FormsModule,
        ReactiveFormsModule,
        NgIf,
        NgFor,
        RouterLink,
        RenderMarkdownComponent,
    ],
})
export class FaqsComponent extends PageComponent implements OnInit {
  public override pageData: FAQs = <FAQs>{};

  public faqs: FAQ[] = [];

  public faqCategories: FAQCategory[] = [];

  public searching = false;

  public searchDone = false;

  public faqSearchResults: FAQ[] = [];

  private timeout!: NodeJS.Timeout;

  public isSearching = false;

  public searchTerm = '';

  public showSearchResults = false;

  public searchForm!: UntypedFormGroup;

  public faqRetrievalError = false;

  public categoryRetrievalError = false;

  private cdr = inject(ChangeDetectorRef);

  private destroyRef = inject(DestroyRef);

  constructor(
    public override readonly titleService: Title,
    public override readonly metaService: Meta,

    public override readonly strapi: StrapiService,
    public readonly formBuilder: UntypedFormBuilder,
    public readonly auth: AuthService
  ) {
    super(titleService, metaService, strapi);
  }

  ngOnInit(): void {
    this.searchForm = this.formBuilder.group({
      searchTerm: [''],
    });

    this.searchForm.get('searchTerm')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((term) => {
      this.searchFAQs(String(term));
    });

    this.strapi
      .getDocumentsByType('faqs-page', {
        populate: {
          mobile_img: { populate: '*' },
        },
      })
      .subscribe((data: IStrapiDocument<FAQs>) => {
        this.parsePageData(data.data.attributes);
        super.setMetas();
        this.cdr.markForCheck();
      });

    // get all the faqs at once, then all the cats, then sort it all out
    // we could get all the cats and then get each faq individually, but that would require dozens of calls
    // so faqs first, then cats after
    this.strapi.getDocumentsByType('faqs', { sort: 'order' }).subscribe({
      next: (faqs: IStrapiDocuments<FAQ>) => {
        faqs.data.forEach((faq: IDocument<FAQ>) => {
          this.faqs.push(<FAQ>{
            id: faq.attributes.id,
            uid: faq.attributes.uid,
            title: faq.attributes.title,
            order: Number(faq.attributes.order),
            answer: faq.attributes.answer,
            top: String(faq.attributes.top).toLowerCase(),
          });
        });

        this.strapi
          .getDocumentsByType('faq-categories', {
            populate: {
              faqs: { populate: '*' },
            },
          })
          .subscribe({
            next: (data: IStrapiDocuments<FAQCategory>) => {
              data.data.forEach((cat: IDocument<FAQCategory>) => {
                const newCat = <FAQCategory>{
                  id: cat.attributes.id,
                  uid: cat.attributes.uid,
                  title: cat.attributes.title,
                  faqs: <FAQ[]>[],
                  order: cat.attributes.order,
                };

                // get the faqs this category is assigned
                // and get their document ids, putting them in an array
                const catFaqs = <any[]>cat.attributes.faqs;
                const catFaqIds = <string[]>[];
                if (catFaqs.length) {
                  catFaqs.forEach((catFaq) => {
                    if (catFaq.faq.data) {
                      // linting disabled because custom cms document structure has no typing
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                      catFaqIds.push(String(catFaq.faq.data.attributes.uid));
                    }
                  });
                }

                // filter the main faqs object, getting only those where their id appears in the array of ids for this category
                newCat.faqs = this.faqs.filter((faq) =>
                  catFaqIds.some((faqId) => faq.uid === faqId)
                );

                this.faqCategories.push(newCat);
                this.faqCategories.sort(
                  (cata, catb) => cata.order - catb.order
                );
              });
            },
            error: () => {
              this.categoryRetrievalError = true;
            },
            complete: () => {
              this.cdr.markForCheck();
            }
          });
      },
      error: () => {
        this.faqRetrievalError = true;
      },
      complete: () => {
        this.cdr.markForCheck();
      }
    });
  }

  protected override parsePageData(page: FAQs): void {
    super.parsePageData(page);
    this.pageData = page;
  }

  public searchFAQs(searchTerm: string): void {
    clearTimeout(this.timeout);

    const regexp = new RegExp(searchTerm, 'i');

    if (searchTerm.length > 2) {
      this.searchTerm = searchTerm;
      this.isSearching = true;
      this.searchDone = false;
      this.timeout = setTimeout(() => {
        this.faqSearchResults = this.faqs.filter((faq) =>
          regexp.test(faq.title)
        );
        this.searchDone = true;
        this.isSearching = false;
        this.showSearchResults = true;
        this.cdr.markForCheck();
      }, 1000);
    }
  }

  public onBlur(): void {
    // don't do it immediately, or the clicks on search results won't work
    setTimeout(() => {
      this.showSearchResults = false;
    }, 100);
  }

  public onFocus(): void {
    this.showSearchResults = true;
  }

  private hideAll(): void {
    this.hideAllFaqCategories();
    this.hideAllFaqs();
  }

  private hideAllFaqCategories(): void {
    const visibleCategories = document.querySelectorAll('.faq-category-show');
    [].forEach.call(visibleCategories, (el: HTMLElement) => {
      el.classList.remove('faq-category-show');
    });
  }

  private hideAllFaqs(): void {
    const visibleFAQs = document.querySelectorAll('.faq-show');
    [].forEach.call(visibleFAQs, (el: HTMLElement) => {
      el.classList.remove('faq-show');
    });
  }

  public toggleFAQCategory(e: Event): void {
    e.stopPropagation();
    e.preventDefault();

    const el = <HTMLElement>e.currentTarget; // the h5
    const parent = <HTMLElement>el.parentElement; // the faq category div

    this.hideAllFaqs();

    // if it's already visible, just remove the class
    if (parent.classList.contains('faq-category-show')) {
      parent.classList.remove('faq-category-show');
    } else {
      // want to show it, all others must be hidden
      this.hideAllFaqCategories();
      parent.classList.add('faq-category-show');
    }
  }

  public toggleFAQ(e: Event): void {
    e.stopPropagation();
    e.preventDefault();

    const el = <HTMLElement>e.currentTarget; // the h5
    const parent = <HTMLElement>el.parentElement; // the faq div

    // if it's already visible, just remove the class
    if (parent.classList.contains('faq-show')) {
      parent.classList.remove('faq-show');
    } else {
      // want to show it, all others must be hidden
      this.hideAllFaqs();
      parent.classList.add('faq-show');
    }
  }

  /**
   * Remove bold markdown syntax from string
   * This is mainly for the faq title, if we try convert the title to markdown it will reutrn a strong tag wrapped in a P tag
   * which is not what we need. This is temporaty until we eventually move to use rich text blocks and making the app and web
   * more consistant with what fields are already supported in code and which arn't
   * @param String markdown
   * @returns String
   */
  public removeBold(markdown: string): string {
    return markdown.replaceAll('**', '');
  }

  /**
   * Remove heading tags from markdown
   * This is will be a temporary fix for the migration, post migration we will need to alter the cms
   * so that the fields that are using these are just
   * @param markdown
   * @returns
   */
  public removeHeading(markdown: string): string {
    return markdown.replaceAll('#', '');
  }
}
